import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';

const useLocationKey = () => {
  const location = useLocation();
  const key = useMemo(() => location.key, [location.key]);
  return key;
};

const ScrollBodyContext = createContext<{
  viewportRef: React.RefObject<HTMLDivElement> | null;
  scrollTo: (id: string) => void;
}>({
  viewportRef: null,
  scrollTo: () => {},
});

export const ScrollBodyProvider = ({
  children,
  viewportRef,
}: PropsWithChildren<{
  viewportRef: React.RefObject<HTMLDivElement> | null;
}>) => {
  const key = useLocationKey();

  useEffect(() => {
    viewportRef?.current?.scrollTo({ top: 0, behavior: 'instant' });
  }, [key, viewportRef]);

  const scrollTo = useCallback(
    (id: string) => {
      const element = document.getElementById(id);
      const offsetTop = element?.offsetTop;

      viewportRef?.current?.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    },
    [viewportRef],
  );

  const value = useMemo(
    () => ({ viewportRef, scrollTo }),
    [viewportRef, scrollTo],
  );

  return (
    <ScrollBodyContext.Provider value={value}>
      {children}
    </ScrollBodyContext.Provider>
  );
};

export const useScrollBody = () => {
  const context = useContext(ScrollBodyContext);

  if (!context) {
    throw new Error('useScrollBody must be used within a ScrollBodyProvider');
  }
  return context;
};
